<script setup lang="ts">
import { onMounted, ref, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import HeaderWithBackButton from '@/components/utilities/HeaderWithBackButton.vue';
import ProjectList from '@/components/rental/projects/ProjectList.vue';
import ProjectGoogleMap from '@/components/maps_canvases/ProjectGoogleMap.vue';
import UnitSelectionWizardSteps from '@/components/forms/UnitSelectionWizardSteps.vue';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls';
import { useProjectsApi } from '@/composables/useProjectsApi';
import { useRentalFilters } from '@/composables/useRentalFilters';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Project } from '@/typings/projects/Project.ts';
import type { Region } from '@/typings/regions/Region.ts';
import type { GetProjectsRequest } from '@/typings/requests/GetProjectsRequest.ts';
import { DEFAULT_RENTAL_UNIT_STATUSES, DEFAULT_RENTAL_VALID_UNIT_STATUSES } from '@/constants/rentalConstants';
import { ListingTypeEnum } from '@/enums/ListingType';
import Map from '../svg/Map.vue';
import List from '../svg/List.vue';
import { InlineSearchBar } from '@/components/utilities/search';

const { t, locale } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});
const { getProjectsByRegion, getProjectsByRegionIdLoading } = useProjectsApi();
const { getProjectWizardUrl, rentalWizardFirstStepUrl } = useRentalLocalizedUrls();
const { loadFiltersFromRentalStorage } = useRentalFilters();

const props = defineProps<{
  region: Region;
}>();



const projects = ref<Project[]>([]);
const showMap = ref(false);
const wizardStep = ref(2);

const isMobile = ref(false);

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 1024;
};

const toggleListsMap = () => {
  if(isMobile.value){
    showMap.value = !showMap.value;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const loadProjects = async () => {
  try {
    const allFilters = loadFiltersFromRentalStorage();
    
    const request: GetProjectsRequest = {
      locale: locale.value.substring(0, 2),
      listingType: 1, // Rental
      regionIds: [props.region.id],
      unitConfigurations: allFilters.unitConfigurations.length > 0 ? allFilters.unitConfigurations : undefined,
      availabilityDates: allFilters.availabilityDates,
      dogAllowed: allFilters.dogAllowed !== null ? allFilters.dogAllowed : undefined,
      floorNumbers: allFilters.floors,
      minPrice: allFilters.minPrice,
      maxPrice: allFilters.maxPrice,
      unitStatuses: DEFAULT_RENTAL_VALID_UNIT_STATUSES
    };
    
    const updatedProjects = await getProjectsByRegion([props.region.id], request);
    projects.value = updatedProjects.sort((a, b) => {
      // Trier d'abord par disponibilité (disponible avant non disponible)
      const aAvailable = a.availableUnits && a.availableUnits > 0 ? 1 : 0;
      const bAvailable = b.availableUnits && b.availableUnits > 0 ? 1 : 0;
      if (aAvailable !== bAvailable) {
        return bAvailable - aAvailable;
      }
      
      // Ensuite, trier par nom de projet
      return a.name.localeCompare(b.name, locale.value);
    });
  } catch (error) {
    console.error('Failed to reload projects with filters:', error);
  }
};

// Fonction pour appliquer les filtres secondaires
const applySecondaryFilters = async () => {
  // Recharger les projets avec les nouveaux filtres
  await loadProjects();
};

// Gestionnaires d'événements pour la carte
const hoveredProject = ref<Project | undefined>(undefined);

// Exposer ListingTypeEnum pour le template
const currentListingType = ListingTypeEnum.Rental;

function onProjectMarkerClick(project: Project) {
  hoveredProject.value = project;
}

// Navigation handlers
function onBackToRegionSelection() {
  window.location.href = rentalWizardFirstStepUrl;
}

function onProjectSelected(project: Project) {
  // Navigation directe vers l'étape suivante
  window.location.href = getProjectWizardUrl(project);
}

onMounted(async () => {
  await loadProjects();
  
  checkMobile();
  window.addEventListener('resize', checkMobile);
});


onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});
</script>

<template>
  <div class="projects-by-region-rental-browsing">
    <!-- Barre de filtres -->
    <InlineSearchBar action-type="filter"
                     :selected-region="props.region.id"
                     @filtersApplied="applySecondaryFilters" />
    <div class="wrapper wrapper_large">
      <!-- Étapes du wizard -->
      <UnitSelectionWizardSteps v-model="wizardStep" />
      <!-- Contenu principal -->
      <div class="main-content">
        <!--En-tête avec bouton de retour-->
        <HeaderWithBackButton :title="t('browsingApp.projects.chooseTheRentalProjectDescription', { count: projects.length, city: props.region.localizedName })"
                              :subtitle="t('browsingApp.projects.chooseTheProjectDescription')"
                              @back-click="onBackToRegionSelection" />
        <div class="twoColumns">
          <div v-if="!isMobile || (isMobile && !showMap)"
               class="projects-content">
            <div class="projects-list">
              <!-- Liste des projets -->
              <div class="projects-list-section">
                <ProjectList :projects="projects"
                             :loading="getProjectsByRegionIdLoading"
                             :listingType="currentListingType"
                             :is-selectable="false"
                             @project-selected="onProjectSelected" />
              </div>
            </div>
          </div>
          <!-- Carte des projets (desktop toujours visible, mobile seulement si showMap = true) -->
          <div v-if="!isMobile || (isMobile && showMap)"
               class="projects-map-section">
            <ProjectGoogleMap
              v-if="projects.length > 0"
              :projects="projects"
              :listingType="currentListingType"
              v-model:hovered-model="hoveredProject"
              @click-marker="onProjectMarkerClick"
              @map-loaded="() => {}" />
          </div>
          <div v-if="isMobile"
               class="button-map-mobile"
               @click="toggleListsMap">
            <span class="icon">
              <List v-if="showMap" />
              <Map v-else />
            </span>
            <span>{{ showMap ? t('browsingApp.projects.viewList') : t('browsingApp.projects.viewMap') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.main-content{
  .twoColumns{
    display: flex;
    flex-direction: row;
    gap: var(--spc24-16);
    width: 100%;
    padding:0 0 var(--spc48-24) 0;
  }

  .projects-content{
    width: 65%;
  }
  .projects-map-section{
    width: 35%;
    display: block;
    position: sticky;
    top: 230px;
    height: calc(100vh - 252px);
  }
}

.button-map-mobile{
  position: sticky;
  bottom: 1.5rem;
  left: -9999px;
  right: -9999px;
  margin: 0 auto;
  color: #fff;
  background-color: #000;
  padding: 1.2rem 1.6rem;
  border-radius: 3rem;
  display:flex;
  gap:0.8rem;
  align-items: center;

  .icon{
    width:1.6rem;
    height:1.6rem;
    display:block;

    svg{
      width: 100%;
      height: 100%;
      display:block;
    }
  }
}

.projectCard-wizardLink {
  display: inline-block;
  margin-top: 12px;
  color: var(--primary-500);
  text-decoration: underline;
  font-size: 1rem;
  font-weight: 600;
  padding: 4px 8px;
  transition: color 0.2s;

  &:hover {
    color: var(--primary-600);
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid var(--primary-300);
    outline-offset: 2px;
  }
}

@media screen and (max-width: 1024px){
  .main-content{
    .twoColumns{
      flex-direction: column;
    }

    .projects-content{
      width: 100%;
    }

    .projects-map-section{
      width: 100%;
      position: relative;
      top: inherit;
      height: 100vh;
    }
  }
}
</style>